import { AttachmentsGrid, AttachmentsGridProps } from 'components/ui/file-uploader/AttachmentsGrid';
import { DocumentTableCardComponent } from 'features/inventory/documents-G4/components/DocumentTableCardComponent';
import { useTranslation } from 'react-i18next';
import { EnhancedCard } from '../card/EnhancedCard';

export interface AutoDocumentConfig {
  /** Il nome del servizio backend dell'entità corrente (es. 'contract', 'customer') */
  serviceName: string;
  /** L'ID numerico dell'entità. Se passato come stringa, verrà convertito in numero */
  entityId: number | null;
  /** Opzionale: Dizionario di parametri custom (es: { contract: contractId, unAltroFiltro: 'valore' }) */
  dictParams?: Record<string, any>;
  /** Opzionale: Stringa descrittiva della cartella (Default: "ID: {entityId}; ") */
  folderDescription?: string;
  /** Opzionale: Blocca le azioni di upload/delete (es. basato su isSupplierRole) */
  disableEdit?: boolean;
  /** Opzionale: Passato come parametro isFornitore al DocumentTableCardComponent */
  isSupplier?: boolean;
  /** Opzionale: Sovrascrittura manuale del nome plurale della cartella (Default: serviceName + 's') */
  customFolderName?: string;

  visible?: boolean;
}

/**
 * Props for the `DocumentsAttachmentsTab` component.
 */
interface DocumentsAttachmentsTabProps {
  documentConfig?: AutoDocumentConfig;
  attachmentsConfig?: AttachmentsGridProps & { visible?: boolean };
}

/**
 * Tab component that renders attachments and the generic document table.
 */
export const DocumentsAttachmentsTab = ({
  documentConfig,
  attachmentsConfig
}: DocumentsAttachmentsTabProps) => {
  const { t } = useTranslation();

  const isDocumentVisible = documentConfig?.visible !== false;
  const isAttachmentsVisible = attachmentsConfig?.visible !== false;

  if ((!isDocumentVisible && !isAttachmentsVisible) || (!documentConfig && !attachmentsConfig)) {
    return null;
  }

  return (
    <>
      {isAttachmentsVisible &&
        attachmentsConfig &&
        attachmentsConfig.entity &&
        (() => {
          const { entity, fields, service, translationPrefix } = attachmentsConfig;
          return (
            <EnhancedCard
              title={t('global.attachments')}
              subtitle={t(
                'global.attachments-subtitle',
                'Visualizza, gestisci e scarica i file digitali e la documentazione associata'
              )}>
              <AttachmentsGrid
                service={service}
                entity={entity}
                fields={fields}
                translationPrefix={translationPrefix}
              />
            </EnhancedCard>
          );
        })()}

      {isDocumentVisible &&
        documentConfig &&
        documentConfig.entityId &&
        (() => {
          const {
            serviceName,
            entityId,
            disableEdit,
            isSupplier,
            customFolderName,
            folderDescription,
            dictParams
          } = documentConfig;
          const folderName = customFolderName || `${serviceName}s`;
          return (
            <DocumentTableCardComponent
              originalService={serviceName}
              objectId={entityId}
              dictParams={dictParams}
              folder={folderName}
              folder_description={folderDescription}
              isFornitore={isSupplier}
              disableEdit={disableEdit}
            />
          );
        })()}
    </>
  );
};
